import type { FieldResult } from '../shared/desktop-api';

/**
 * Paige's words for a document held because a value had a problem (the engine's `field-problem`,
 * ReviewState.HasFieldProblem). CapturePoint says "a value the reader had a problem with counts as
 * 0 against its field threshold", which tells a customer how the threshold works and not which value
 * is wrong (Dustin, 2026-10-01, a Form W-9 held for its Taxpayer Identification Number). This names
 * the field and the problem the engine judged it on, in the engine's order: a value set aside, a
 * format that does not match, a value the reader doubted, a value printed nowhere on the page.
 */

/** What the reason line needs of a field. */
export type ProblemField = Pick<FieldResult, 'name' | 'value'> &
  Partial<
    Pick<
      FieldResult,
      | 'needsReview'
      | 'verified'
      | 'fieldKind'
      | 'source'
      | 'validationStatus'
      | 'validationMessage'
      | 'evidenceLineIndexes'
      | 'barcodeEvidence'
      | 'lookupEvidence'
    >
  >;

export type ProblemDocument = {
  fields?: readonly ProblemField[] | null;
  validationFindings?: readonly { code?: string | null }[] | null;
};

/** DocumentPipeline.MisplacedValueSource: a value printed where another field's label points. */
const SET_ASIDE = 'set aside - printed where another field';
/** FindingCode.UngroundedExtraction. */
const UNGROUNDED = 'ungrounded_extraction';
/** The format check's own words (FieldValidation). */
const FORMAT = 'Does not match the expected format';

type Problem = 'set-aside' | 'format' | 'doubt' | 'not-on-page';

/** The end of "Needs review: …" for a field problem; the general words when no field can be named. */
export function fieldProblemWords(document: ProblemDocument): string {
  const problems = problemFields(document);
  const first = problems[0];
  if (!first) return 'Cortexa couldn’t read one of its values cleanly';
  if (problems.length > 1)
    return `Cortexa couldn’t read ${problems.length} values cleanly, starting with ${first.field.name}`;
  return SENTENCES[first.problem](first.field.name);
}

const SENTENCES: Record<Problem, (name: string) => string> = {
  'set-aside': (name) =>
    `Cortexa found ${name} printed under a different label and left it for you to check`,
  format: (name) => `${name} doesn’t match the format it should have`,
  doubt: (name) => `Cortexa couldn’t read ${name} cleanly`,
  'not-on-page': (name) => `Cortexa couldn’t find ${name} printed on the page`,
};

/** The fields the engine would judge at confidence 0, each with the first problem it has. */
export function problemFields(
  document: ProblemDocument,
): { field: ProblemField; problem: Problem }[] {
  const ungrounded = (document.validationFindings ?? []).some(
    (finding) => finding.code?.toLowerCase() === UNGROUNDED,
  );
  return (document.fields ?? []).flatMap((field) => {
    const problem = judged(field) ? problemOf(field, ungrounded) : null;
    return problem ? [{ field, problem }] : [];
  });
}

/** A field the field threshold judges at all: not the type, not a table, not one a person confirmed. */
const judged = (field: ProblemField): boolean =>
  field.name.toLowerCase() !== 'document type' &&
  field.fieldKind?.toLowerCase() !== 'line-items' &&
  !field.verified;

function problemOf(field: ProblemField, ungrounded: boolean): Problem | null {
  if (sourceStarts(field, SET_ASIDE)) return 'set-aside';
  if (!field.value?.trim()) return null;
  if (badFormat(field)) return 'format';
  if (field.needsReview) return 'doubt';
  return ungrounded && !printed(field) && !sourceStarts(field, 'operator') ? 'not-on-page' : null;
}

const sourceStarts = (field: ProblemField, prefix: string): boolean =>
  (field.source ?? '').toLowerCase().startsWith(prefix);

const badFormat = (field: ProblemField): boolean =>
  field.validationStatus === 'warning' && Boolean(field.validationMessage?.startsWith(FORMAT));

/** Something vouches for the value: OCR lines it was read from, a barcode, or a lookup. */
const printed = (field: ProblemField): boolean =>
  (field.evidenceLineIndexes?.length ?? 0) > 0 ||
  Boolean(field.barcodeEvidence) ||
  Boolean(field.lookupEvidence);
